import React, { useState, useEffect } from 'react';
import * as Form from '@radix-ui/react-form';
import { Mail, User, Lock, Loader2, CheckCircle, XCircle } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { userService } from '../../services/userService';
import { useError } from '../../context/ErrorContext';

const Settings = () => {
  const { user, login } = useAuth();
  const { showError } = useError();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    const fetchUserData = async () => {
      if (user?.userId) {
        try {
          const userData = await userService.getUserById(user.userId);
          if (userData) {
            setName(userData.name || '');
            setEmail(userData.email || '');
          }
        } catch (error) {
          console.error('Error fetching user data:', error);
          showError('Failed to load user data.', 'error');
        }
      }
    };
    fetchUserData();
  }, [user?.userId, showError]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setMessage(null);

    if (!name || !email) {
      setMessage({ type: 'error', text: 'Name and Email are required.' });
      setIsLoading(false);
      return;
    }

    if (newPassword && newPassword.length < 6) {
      setMessage({ type: 'error', text: 'New password must be at least 6 characters.' });
      setIsLoading(false);
      return;
    }

    if (newPassword && newPassword !== confirmNewPassword) {
      setMessage({ type: 'error', text: 'New password and confirmation do not match.' });
      setIsLoading(false);
      return;
    }

    try {
      try {
        if (!currentPassword) {
          setMessage({ type: 'error', text: 'Current password is required to update your profile.' });
          setIsLoading(false);
          return;
        }
        await login(user.email, currentPassword);
      } catch (authError) {
        setMessage({ type: 'error', text: 'Incorrect current password. Please try again.' });
        setIsLoading(false);
        return;
      }

      const updateData = { name, email };
      if (newPassword) {
        updateData.newPassword = newPassword;
      }

      await userService.updateUser(user.userId, updateData);

      setMessage({ type: 'success', text: 'Profile updated successfully!' });
      setNewPassword('');
      setConfirmNewPassword('');
      setCurrentPassword(''); 
    } catch (error) {
      console.error('Failed to update profile:', error);
      setMessage({ type: 'error', text: error.message || 'Failed to update profile. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex justify-center items-start py-6 sm:py-8 lg:py-2 bg-gray-100 dark:bg-gray-900 px-4 sm:px-6 lg:px-2">
      <div className="
        w-full
        p-6 rounded-lg shadow-md bg-white dark:bg-gray-800 max-w-full
        md:p-8 md:max-w-4xl
        lg:max-w-full lg:p-10
      ">
        <h2 className="text-3xl font-extrabold text-gray-900 dark:text-white mb-8 text-center">
          Profile Settings
        </h2>

        {message && (
          <div className={`p-4 mb-6 rounded-lg flex items-center gap-3 ${
            message.type === 'success'
              ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
              : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
          }`}>
            {message.type === 'success' ? <CheckCircle className="h-6 w-6" /> : <XCircle className="h-6 w-6" />}
            <p className="text-base font-medium">{message.text}</p>
          </div>
        )}

        <Form.Root className="space-y-8" onSubmit={handleSubmit}>
          {/* Profile Information Section */}
          <div className="border-b border-gray-200 dark:border-gray-700 pb-6">
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-5">Personal Information</h3>
            <div className="space-y-5">
              <Form.Field name="name">
                <Form.Label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Name</Form.Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                  <Form.Control asChild>
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="w-full pl-11 pr-4 py-2.5 border border-gray-300 dark:border-gray-700 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-accent focus:border-accent bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 transition duration-150 ease-in-out"
                      required
                    />
                  </Form.Control>
                </div>
              </Form.Field>

              <Form.Field name="email">
                <Form.Label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Email Address</Form.Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                  <Form.Control asChild>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full pl-11 pr-4 py-2.5 border border-gray-300 dark:border-gray-700 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-accent focus:border-accent bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 transition duration-150 ease-in-out"
                      required
                    />
                  </Form.Control>
                </div>
              </Form.Field>
            </div>
          </div>

          {/* Change Password Section */}
          <div className="pt-6">
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-5">Change Password</h3>
            <div className="space-y-5">
              <Form.Field name="currentPassword">
                <Form.Label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Current Password</Form.Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                  <Form.Control asChild>
                    <input
                      type="password"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      className="w-full pl-11 pr-4 py-2.5 border border-gray-300 dark:border-gray-700 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-accent focus:border-accent bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 transition duration-150 ease-in-out"
                      placeholder="Required to update any profile info"
                      required
                    />
                  </Form.Control>
                </div>
              </Form.Field>

              <Form.Field name="newPassword">
                <Form.Label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">New Password (optional)</Form.Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                  <Form.Control asChild>
                    <input
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="w-full pl-11 pr-4 py-2.5 border border-gray-300 dark:border-gray-700 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-accent focus:border-accent bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 transition duration-150 ease-in-out"
                      placeholder="Leave blank to keep current password"
                    />
                  </Form.Control>
                </div>
              </Form.Field>

              <Form.Field name="confirmNewPassword">
                <Form.Label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Confirm New Password</Form.Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                  <Form.Control asChild>
                    <input
                      type="password"
                      value={confirmNewPassword}
                      onChange={(e) => setConfirmNewPassword(e.target.value)}
                      className="w-full pl-11 pr-4 py-2.5 border border-gray-300 dark:border-gray-700 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-accent focus:border-accent bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 transition duration-150 ease-in-out"
                      placeholder="Confirm your new password"
                    />
                  </Form.Control>
                </div>
              </Form.Field>
            </div>
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className={`w-full flex justify-center items-center py-3 px-6 border border-transparent rounded-lg shadow-sm text-base font-semibold text-white bg-accent hover:bg-primary-700 focus:outline-none focus:ring-3 focus:ring-offset-2 focus:ring-accent transition duration-150 ease-in-out ${
              isLoading ? 'opacity-70 cursor-not-allowed' : ''
            }`}
          >
            {isLoading ? (
              <>
                <Loader2 className="animate-spin -ml-1 mr-3 h-5 w-5" />
                Saving Changes...
              </>
            ) : (
              'Save Changes'
            )}
          </button>
        </Form.Root>
      </div>
    </div>
  );
};

export default Settings;